import { Component, ChangeDetectionStrategy, inject, signal, effect } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { filter, map, startWith } from "rxjs/operators";

import { BreadcrumbModule } from "primeng/breadcrumb";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { MenuItem } from "primeng/api";
import { LocaleService } from "src/app/shared/util/locale.service";

@Component({
    selector: "app-breadcrumb",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BreadcrumbModule, TranslateModule],
    templateUrl: "./breadcrumb.component.html",
    styleUrl: "./breadcrumb.component.scss",
})
export class BreadcrumbComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly locale = inject(LocaleService);

    // =========================================================================
    // ROUTE LABELS MAPPING
    // =========================================================================
    private readonly routeLabels: Record<string, string> = {
        // Home routes
        "home": "BREADCRUMB.HOME",
        "shortcuts": "BREADCRUMB.SHORTCUTS",
        "exchange-rates": "BREADCRUMB.EXCHANGE_RATES",

        // Quote routes
        "quote": "BREADCRUMB.QUOTES",
        "create": "BREADCRUMB.CREATE",
        "sales-order": "BREADCRUMB.SALES_ORDERS",
        "create-subscription": "BREADCRUMB.CREATE_SUBSCRIPTION",
        "overview": "BREADCRUMB.OVERVIEW",
        "quote-info": "BREADCRUMB.QUOTE_INFO",
        "billing": "BREADCRUMB.BILLING",
        "delivery": "BREADCRUMB.DELIVERY",
        "notes": "BREADCRUMB.NOTES",
        "items": "BREADCRUMB.ITEMS",
        "catalogs": "BREADCRUMB.CATALOGS",
        "startup-info": "BREADCRUMB.STARTUP_INFO",
        "plants": "BREADCRUMB.PLANTS",
        "services": "BREADCRUMB.SERVICES",
        "freight": "BREADCRUMB.FREIGHT",
        "brand-information": "BREADCRUMB.BRAND_INFORMATION",
        "terms": "BREADCRUMB.TERMS",
        "inquiries": "BREADCRUMB.INQUIRIES",
        "versions": "BREADCRUMB.VERSIONS",
        "preview": "BREADCRUMB.PREVIEW",

        // Developer routes
        "developer": "BREADCRUMB.DEVELOPER",
        "notifications": "BREADCRUMB.NOTIFICATIONS",
        "transfer-quotes": "BREADCRUMB.TRANSFER_QUOTES",
        "auto-archive": "BREADCRUMB.AUTO_ARCHIVE",
        "marketing-automation": "BREADCRUMB.MARKETING_AUTOMATION",
        "loader-preview": "BREADCRUMB.LOADER_PREVIEW",
        "attio-quote-migration": "BREADCRUMB.ATTIO_QUOTE_MIGRATION",
        "attio-migration": "BREADCRUMB.ATTIO_MIGRATION",

        // Super user routes
        "super-user": "BREADCRUMB.SUPER_USER",
        "contracts": "BREADCRUMB.CONTRACTS",
        "currency-table": "BREADCRUMB.CURRENCY",
        "interest-rate": "BREADCRUMB.INTEREST_RATE",

        // Contract detail routes
        "contract": "BREADCRUMB.CONTRACTS",
        "discount": "CONTRACT.DETAIL.TABS.DISCOUNTS",

        // Central support routes
        "central-support": "BREADCRUMB.CENTRAL_SUPPORT",

        // Customer routes
        "customer": "BREADCRUMB.CUSTOMER",

        // Vendor routes
        "vendor": "BREADCRUMB.VENDOR",
    };

    // =========================================================================
    // LOCAL STATE
    // =========================================================================
    public readonly items = signal<MenuItem[]>([]);

    public readonly home: MenuItem = {
        icon: "pi pi-home",
        routerLink: "/",
    };

    // =========================================================================
    // ROUTE SIGNALS
    // =========================================================================
    private readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(event => event.urlAfterRedirects),
            startWith(this.router.url),
        ),
        { initialValue: this.router.url }
    );

    // =========================================================================
    // EFFECTS
    // =========================================================================
    private readonly buildBreadcrumbsEffect = effect(() => {
        const url = this.currentUrl();
        // Track translation changes so labels rebuild when translations load or language changes
        this.locale.translationChange();
        if (url) {
            this.buildBreadcrumbs(url);
        }
    });
    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================
    private buildBreadcrumbs(url: string): void {
        // Remove query params and fragment
        const cleanUrl = url.split("?")[0].split("#")[0];

        // Split URL into segments
        const segments = cleanUrl.split("/").filter(segment => segment.length > 0);

        if (segments.length === 0) {
            this.items.set([]);
            return;
        }

        const breadcrumbs: MenuItem[] = [];
        let currentPath = "";

        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            currentPath += `/${segment}`;

            // Skip company ID (6-digit number at the start)
            if (i === 0 && /^\d{6}$/.test(segment)) {
                continue;
            }

            // Skip super-user root segment (children show directly like top-level routes)
            if (segment === "super-user") {
                continue;
            }

            // Sales order numbers may be alphanumeric; treat them like other detail identifiers.
            const prevSegment = segments[i - 1];
            if (/^\d+$/.test(segment) || prevSegment === "sales-order") {
                // Get the previous segment to determine context
                if (prevSegment === "quote") {
                    // The quote number is the toolbar's job — keep the breadcrumb a
                    // pure path (Home › Quotes › <tab>) and skip the id segment.
                    continue;
                } else if (prevSegment === "release-notes") {
                    // Notification detail - show generic label
                    breadcrumbs.push({
                        label: this.translate.instant("BREADCRUMB.DETAIL"),
                        routerLink: currentPath,
                    });
                } else {
                    // Generic ID - skip or show as ID
                    breadcrumbs.push({
                        label: `#${segment}`,
                        routerLink: currentPath,
                    });
                }
                continue;
            }

            // Check if we have a label mapping for this segment
            const translationKey = this.routeLabels[segment];
            if (translationKey) {
                breadcrumbs.push({
                    label: this.translate.instant(translationKey),
                    routerLink: currentPath,
                });
            }
        }

        // Make only the actual current page non-clickable. When the quote id is
        // skipped, the last crumb is a parent (e.g. Quotes) and must stay clickable.
        const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
        if (lastCrumb && lastCrumb.routerLink === cleanUrl) {
            delete lastCrumb.routerLink;
        }

        this.items.set(breadcrumbs);
    }
}
